import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useTranslation } from 'react-i18next'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { getPriorityChain, processNovelty } from '../../../services/CompaniesService'
import { CompanyNoveltiesType } from '../../../types'

// Mismo criterio que isBlockingStatus del backend: los estados terminales
// (Procesado/Cancelado/Rechazado/Inactivo) liberan la cadena; el resto bloquea.
const NS_SUCCESS = 5
const NS_ERROR = 4
const isBlockingStatus = (status: number) =>
  status === 0 || status === 3 || status === 4 || status === 6

// Cooldown de 30' entre prioridades: la fila hereda cooldown_until del backend
// (calculado a partir de cuándo se procesó exitosamente la prioridad anterior).
const isInCooldown = (row: CompanyNoveltiesType, now: number) =>
  Boolean(row.cooldown_until && new Date(row.cooldown_until).getTime() > now)

const formatCountdown = (row: CompanyNoveltiesType, now: number) => {
  const remainingMs = new Date(row.cooldown_until as string).getTime() - now
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

type PriorityChainModalType = {
  openModal: boolean
  companyId: string
  currentNoveltyId: string
  chain: CompanyNoveltiesType[]
  onClose: () => void
  // Se invoca cuando la novedad que abrió el modal (currentNoveltyId) cambia
  // de estado durante el polling, para que la página de detalle por debajo
  // (que no vuelve a pedir datos por su cuenta) refresque su propia vista.
  onNoveltyUpdated?: () => void
}

const PriorityChainModal: React.FC<PriorityChainModalType> = ({
  openModal,
  companyId,
  currentNoveltyId,
  chain,
  onClose,
  onNoveltyUpdated,
}) => {
  const { t } = useTranslation('priorityChain')
  const navigate = useNavigate()
  const { getAccessTokenSilently } = useAuth0()

  const [rows, setRows] = useState<CompanyNoveltiesType[]>(chain)
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [showProcessed, setShowProcessed] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  const pollRef = React.useRef<number>()
  const currentStatusRef = React.useRef<number | undefined>(
    chain.find(r => r.id === currentNoveltyId)?.status,
  )

  React.useEffect(() => {
    setRows(chain)
  }, [chain])

  // Limpiar el polling al desmontar el modal.
  React.useEffect(() => {
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current)
    }
  }, [])

  const fetchChain = async () => {
    const token = await getAccessTokenSilently()
    const res = await getPriorityChain(token, companyId, currentNoveltyId)
    setRows(res.data.chain)

    const current = res.data.chain.find((r: CompanyNoveltiesType) => r.id === currentNoveltyId)
    if (current && current.status !== currentStatusRef.current) {
      currentStatusRef.current = current.status
      onNoveltyUpdated?.()
    }

    return res.data.chain
  }

  // Una fila ya procesada nunca bloquea; el resto bloquea por status.
  const isRowBlocked = (row: CompanyNoveltiesType) =>
    row.status !== NS_SUCCESS && isBlockingStatus(row.status)

  // La primera fila no resuelta es la que está en turno: la única que puede
  // procesarse, o esperar el cooldown si la prioridad anterior se procesó recién.
  const firstBlockingIdx = rows.findIndex(isRowBlocked)

  // Solo la fila en turno espera el cooldown. El backend lo manda también en las
  // de más atrás, pero esas siguen bloqueadas por las prioridades intermedias.
  const currentRow = firstBlockingIdx >= 0 ? rows[firstBlockingIdx] : undefined
  const hasActiveCooldown = Boolean(currentRow && isInCooldown(currentRow, now))

  // Tick de 1s para la cuenta regresiva. Solo corre mientras la fila en turno
  // esté esperando, para no mantener un interval ocioso. Al expirar, se dispara
  // un refetch para confirmar contra el backend (fuente de verdad del cooldown)
  // y habilitar "Procesar ahora" con datos frescos.
  React.useEffect(() => {
    if (!hasActiveCooldown) return
    const id = window.setInterval(() => {
      const stillInCooldown = Boolean(currentRow && isInCooldown(currentRow, Date.now()))
      setNow(Date.now())
      if (!stillInCooldown) fetchChain()
    }, 1000)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveCooldown])

  const handleProcessRow = async (id: string) => {
    setProcessingId(id)
    try {
      const token = await getAccessTokenSilently()
      await processNovelty(token, companyId, { id })
    } catch (e) {
      // Si la request de procesamiento falla de entrada, refrescamos y salimos.
      setProcessingId(null)
      await fetchChain()
      return
    }

    // El procesamiento en backend es asíncrono y puede tardar (sube a VISMA,
    // valida y espera el log). Hacemos polling de la cadena hasta que la fila
    // deje de estar en un estado en curso (pasa a Procesado o Error), con un
    // tope de seguridad por si nunca resuelve.
    const startedAt = Date.now()
    const maxWaitMs = 3 * 60 * 1000 // 3 minutos
    if (pollRef.current) window.clearInterval(pollRef.current)
    pollRef.current = window.setInterval(async () => {
      try {
        const updated = await fetchChain()
        const row = updated.find(r => r.id === id)
        const settled = !row || row.status === NS_SUCCESS || row.status === NS_ERROR
        if (settled || Date.now() - startedAt > maxWaitMs) {
          if (pollRef.current) window.clearInterval(pollRef.current)
          pollRef.current = undefined
          setProcessingId(null)
        }
      } catch (e) {
        if (pollRef.current) window.clearInterval(pollRef.current)
        pollRef.current = undefined
        setProcessingId(null)
      }
    }, 5000)
  }

  // "Subir nuevamente" edita la misma novedad en Error en vez de crear una
  // copia: navega a su detalle en modo reupload, donde solo Tipo y Archivo
  // quedan editables para reintentar el procesamiento con un archivo nuevo.
  // `anchor` es la novedad que abrió este modal: al guardar el reupload, el
  // formulario vuelve ahí y reabre la cadena para que el usuario continúe
  // procesando en orden sin perder el contexto.
  const handleReupload = (row: CompanyNoveltiesType) => {
    navigate(`/novedades/novedad/${row.id}?reupload=1&anchor=${currentNoveltyId}`)
  }

  const isWaitingCooldown = (row: CompanyNoveltiesType, idx: number) =>
    idx === firstBlockingIdx && isInCooldown(row, now)

  const renderStatus = (row: CompanyNoveltiesType, idx: number) => {
    if (row.status === NS_SUCCESS) {
      return <Chip size="small" color="success" label={t('status.processed')} />
    }
    if (row.status === NS_ERROR) {
      return <Chip size="small" color="error" label={t('status.error')} />
    }
    if (processingId === row.id) {
      return <Chip size="small" color="info" label={t('status.processing')} />
    }
    if (isWaitingCooldown(row, idx)) {
      return (
        <Chip
          size="small"
          color="warning"
          label={t('status.availableIn', { countdown: formatCountdown(row, now) })}
        />
      )
    }
    if (idx === firstBlockingIdx) {
      return <Chip size="small" color="warning" label={t('status.pending')} />
    }
    // Bloqueada por las prioridades anteriores todavía no resueltas. Se listan
    // sin repetir: con varias novedades por prioridad, alcanza con nombrarla una vez.
    const blockers = Array.from(
      new Set(
        rows
          .slice(0, idx)
          .filter(isRowBlocked)
          .map(r => `P${r.priority}`),
      ),
    ).join(' ')
    const isCurrent = row.id === currentNoveltyId
    return (
      <Chip
        size="small"
        color="default"
        label={
          t('status.blockedBy', { blockers }) +
          (isCurrent ? t('status.currentNoveltySuffix') : '')
        }
      />
    )
  }

  const renderAction = (row: CompanyNoveltiesType, idx: number) => {
    if (row.status === NS_ERROR) {
      return (
        <Button size="small" variant="outlined" onClick={() => handleReupload(row)}>
          {t('actions.reupload')}
        </Button>
      )
    }
    if (isWaitingCooldown(row, idx)) {
      return <span>—</span>
    }
    if (idx === firstBlockingIdx && row.status !== NS_SUCCESS) {
      const isProcessingThis = processingId === row.id
      return (
        <Button
          size="small"
          variant="contained"
          disabled={processingId !== null}
          onClick={() => handleProcessRow(row.id)}
          startIcon={isProcessingThis ? <CircularProgress size={16} /> : undefined}
        >
          {isProcessingThis ? t('actions.processing') : t('actions.processNow')}
        </Button>
      )
    }
    return <span>—</span>
  }

  // Estado global de la cadena para el aviso de cierre.
  const hasRows = rows.length > 0
  const allProcessed = hasRows && rows.every(r => r.status === NS_SUCCESS)
  const hasError = rows.some(r => r.status === NS_ERROR)

  // Las filas ya Procesadas se colapsan por defecto: con historial largo, ver
  // todas las novedades exitosas repetidas tapa lo que realmente importa (lo
  // pendiente/bloqueado/en error). Se conserva el índice original de `rows`
  // para que renderStatus/renderAction (que dependen de firstBlockingIdx sobre
  // la lista completa) sigan calculando bien los bloqueos.
  const indexedRows = rows.map((row, idx) => ({ row, idx }))
  const processedEntries = indexedRows.filter(({ row }) => row.status === NS_SUCCESS)
  const relevantEntries = indexedRows.filter(({ row }) => row.status !== NS_SUCCESS)

  return (
    <Dialog open={openModal} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: '24px' }}>{t('title')}</DialogTitle>
      <DialogContent>
        {!allProcessed && (
          <DialogContentText sx={{ mb: 2, color: '#333333', fontSize: '14px' }}>
            {t('pendingMessage')}
          </DialogContentText>
        )}
        {allProcessed && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('allProcessedMessage')}
          </Alert>
        )}
        {hasActiveCooldown && currentRow && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('cooldownMessage', { countdown: formatCountdown(currentRow, now) })}
          </Alert>
        )}
        {!allProcessed && hasError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('errorWarningMessage')}
          </Alert>
        )}
        {processedEntries.length > 0 && (
          <Button
            size="small"
            onClick={() => setShowProcessed(v => !v)}
            startIcon={showProcessed ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ mb: 1 }}
          >
            {showProcessed ? t('hide') : t('show')}{' '}
            {t('processedCount', { count: processedEntries.length })}
          </Button>
        )}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('columns.novelty')}</TableCell>
                <TableCell align="center">{t('columns.type')}</TableCell>
                <TableCell align="center">{t('columns.priority')}</TableCell>
                <TableCell>{t('columns.status')}</TableCell>
                <TableCell align="right">{t('columns.action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {showProcessed &&
                processedEntries.map(({ row, idx }) => (
                  <TableRow
                    key={row.id}
                    sx={
                      row.id === currentNoveltyId
                        ? { backgroundColor: '#EEF1FC' }
                        : undefined
                    }
                  >
                    <TableCell>
                      <Link
                        href={`/novedades/novedad/${row.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                      >
                        {row.name}
                      </Link>
                    </TableCell>
                    <TableCell align="center">
                      {row.novelty_type?.novelty_type?.code ?? '—'}
                    </TableCell>
                    <TableCell align="center">{row.priority}</TableCell>
                    <TableCell>{renderStatus(row, idx)}</TableCell>
                    <TableCell align="right">{renderAction(row, idx)}</TableCell>
                  </TableRow>
                ))}
              {relevantEntries.map(({ row, idx }) => (
                <TableRow
                  key={row.id}
                  sx={
                    row.id === currentNoveltyId
                      ? { backgroundColor: '#EEF1FC' }
                      : undefined
                  }
                >
                  <TableCell>
                    <Link
                      href={`/novedades/novedad/${row.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      {row.name}
                    </Link>
                  </TableCell>
                  <TableCell align="center">
                    {row.novelty_type?.novelty_type?.code ?? '—'}
                  </TableCell>
                  <TableCell align="center">{row.priority}</TableCell>
                  <TableCell>{renderStatus(row, idx)}</TableCell>
                  <TableCell align="right">{renderAction(row, idx)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '18px' }}>
        <Button variant="outlined" onClick={onClose}>
          {t('actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PriorityChainModal
